// src/features/admin/NavFeaturesPanel.jsx
//
// ADMIN-NAV-FEATURE-TOGGLES: Admin panel for live feature flag management.
//
// ADMIN-NAV-TOGGLES-V2 enhancements:
//   - Toggles call updateFlag() from FeatureFlagContext so NavRail re-renders
//     instantly in the same tab without a page reload.
//   - Left-nav rows are drag-to-reorder (HTML5 draggable, no new dep).
//     Drop calls PATCH /api/admin/feature-flags/reorder; reverts on error.
//   - Ask Portfolio uses its own magnifier icon (FLAG_KEY_TO_NAV_KEY updated).
//   - Preview-mode description updated to three-case wording.
//
// LOCATION-TABS-CONTROL + NAV-RESTORE-DEFAULTS enhancements:
//   - "Location tabs" section: one row per location_tab flag; draggable to
//     reorder, with toggle (all-tabs-hidden guard enforced server-side).
//   - "Restore defaults" button opens an inline confirmation modal; on confirm
//     POSTs to /api/admin/feature-flags/restore-defaults and reloads all flags.
//
// Layout:
//   - "Preview mode" master toggle at the top.
//   - "Left navigation" section: drag-to-reorder rows.
//   - "Top navigation" section: non-reorderable rows.
//   - "Location tabs" section: drag-to-reorder rows.
//   - "Restore defaults" button below the sections.

import React, { useEffect, useState, useCallback, useRef } from "react";
import { T } from "../../app/tokens.js";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import { NavRailIcon } from "../../components/NavRailIcon.jsx";
import { AdminSubSidebarIcon } from "../../components/AdminSubSidebarIcon.jsx";
import { useFeatureFlagContext } from "../../contexts/FeatureFlagContext.jsx";
import { log } from "../../utils/logger.js";
import { dbNavKey, legacyFlagForNav } from "../../app/governedNav.js";
import { PROJECTS_TABS } from "../../app/projectsTabs.js";
import { PLACES_LENSES } from "../../app/places.js";
import {
  emitAttendanceSourceChange,
} from "../../services/cacheBus.js";

// ─── Constants ────────────────────────────────────────────────────────────────

// DB key → FEATURE_ flag name in the context map.
// Used to call updateFlag() with the right context key after a nav toggle.
const DB_KEY_TO_FLAG_KEY = {
  ask_portfolio:         "FEATURE_ASK_PORTFOLIO_NAV",
  in_focus_panel:        "FEATURE_IN_FOCUS_PANEL",
  actions:               "FEATURE_ACTIONS_BELL",
  preview_mode:          "preview_mode",
  "pulse.in_focus_layout": "IN_FOCUS_LAYOUT",
  // RADAR-PHASE-1-SPINE: three-band Radar page (setting surface, shipped dark).
  radar_spine:           "FEATURE_RADAR_SPINE",
  radar_decisions_register: "FEATURE_RADAR_DECISIONS_REGISTER",
  markets_strategy_tabs: "FEATURE_MARKETS_STRATEGY_TABS",
};

// ─── Icon mapping ─────────────────────────────────────────────────────────────

// Map from feature_flags.key → NavRailIcon itemKey so we get the right icon.
const FLAG_KEY_TO_NAV_KEY = {
  // Left-nav items
  pulse:           "home",
  // NAV-RESTRUCTURE-RADAR: radar and playbooks nav keys.
  radar:           "radar",
  playbooks:       "playbooks",
  locations:       "portfolio",
  leases:          "leases",
  signals:         "signals",
  reports:         "reports",
  plan:            "plan",
  projects:        "projects",
  ask_portfolio:   "ask_portfolio",
  roadmap:         "roadmap",
  workflows:       "workflows",
  decisions:       "decisions",
  reviews:         "reviews",
  // Location-tab items (reuse nav icons as generic visual markers)
  tab_overview:    "portfolio",
  tab_utilization: "plan",
  tab_experience:  "portfolio",
  tab_service:     "portfolio",
  tab_intel:       "portfolio",
  // Report items (reuse nav icons as generic visual markers)
  "report-wpr":                "roadmap",
};

// ─── Shared sub-components ────────────────────────────────────────────────────

// Slim toggle (§7n style). Keyboard-accessible via role="switch".
function SlimToggle({ checked, onChange, disabled, testId }) {
  const TRACK_W = 36;
  const TRACK_H = 20;
  const KNOB_D  = 14;

  return (
    <button
      role="switch"
      aria-checked={checked}
      data-testid={testId}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      style={{
        width: TRACK_W, height: TRACK_H, borderRadius: TRACK_H / 2,
        background: checked ? T.blue : T.borderDark,
        border: "none", cursor: disabled ? "not-allowed" : "pointer",
        position: "relative", flexShrink: 0, transition: "background 0.15s",
        outline: "none", padding: 0,
      }}
    >
      <span
        style={{
          position: "absolute",
          top: (TRACK_H - KNOB_D) / 2,
          left: checked ? TRACK_W - KNOB_D - 3 : 3,
          width: KNOB_D, height: KNOB_D,
          borderRadius: "50%",
          background: T.white,
          transition: "left 0.15s",
        }}
      />
    </button>
  );
}

// Six-dot drag grip icon (inline SVG, no new dep).
function DragHandle() {
  return (
    <svg
      data-testid="drag-handle"
      width={14} height={20}
      viewBox="0 0 14 20"
      fill="currentColor"
      style={{
        display: "block", flexShrink: 0,
        color: T.borderDark, cursor: "grab",
      }}
      aria-hidden="true"
    >
      <circle cx="4" cy="5"  r="1.5" />
      <circle cx="10" cy="5" r="1.5" />
      <circle cx="4" cy="10" r="1.5" />
      <circle cx="10" cy="10" r="1.5" />
      <circle cx="4" cy="15" r="1.5" />
      <circle cx="10" cy="15" r="1.5" />
    </svg>
  );
}

// One feature-flag row (optional drag handle, icon, label, description, toggle).
// `useAdminIcon` swaps NavRailIcon for AdminSubSidebarIcon (admin_section rows
// reuse AdminPage's own icon set instead of the top-level nav icon set).
// `locked` rows show a lock badge + tooltip and always render a disabled toggle.
function FlagRow({ flag, onToggle, busy, draggable, onDragStart, onDragOver, onDrop, onDragEnd, useAdminIcon }) {
  const navKey = flag.surface === "left_nav"
    ? dbNavKey(flag.key)
    : FLAG_KEY_TO_NAV_KEY[flag.key] ?? flag.key;
  const locked = !!flag.locked;
  const movable = !locked || flag.surface === "project_tab";
  return (
    <div
      data-testid={`flag-row-${flag.key}`}
      draggable={draggable && movable}
      onDragStart={draggable && movable ? onDragStart : undefined}
      onDragOver={draggable && movable ? onDragOver : undefined}
      onDrop={draggable && movable ? onDrop : undefined}
      onDragEnd={draggable && movable ? onDragEnd : undefined}
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "10px 0",
        borderBottom: `1px solid ${T.border}`,
        cursor: draggable && !locked ? "default" : undefined,
      }}
    >
      {/* Drag handle — only for draggable, unlocked rows */}
      {draggable && (
        <span style={{ display: "flex", alignItems: "center", flexShrink: 0, visibility: movable ? "visible" : "hidden" }}>
          <DragHandle />
        </span>
      )}

      {/* Nav icon */}
      <span style={{
        width: 28, height: 28, borderRadius: 8,
        background: T.lightGray,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0, color: T.mid,
      }}>
        {useAdminIcon
          ? <AdminSubSidebarIcon itemKey={flag.key} size={15} />
          : <NavRailIcon itemKey={flag.surface === "project_tab" ? "projects" : navKey} filled={false} size={15} />}
      </span>

      {/* Label + description */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: T.fsBase, fontWeight: T.fwSemibold, color: T.dark }}>
            {flag.label}
          </span>
          {locked && (
            <span
              data-testid={`flag-locked-badge-${flag.key}`}
              title="This section is always visible and can't be hidden."
              style={{
                fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.mid,
                border: `1px solid ${T.border}`, borderRadius: 4,
                padding: "1px 6px", cursor: "default",
              }}
            >
              Locked
            </span>
          )}
        </div>
        {flag.description && (
          <div style={{ fontSize: T.fsSm, color: T.mid, marginTop: 2 }}>
            {flag.description}
          </div>
        )}
      </div>

      {/* Toggle */}
      <span title={locked ? "This section is always visible and can't be hidden." : undefined}>
        <SlimToggle
          checked={flag.enabled}
          onChange={(val) => onToggle(flag.key, val)}
          disabled={busy || locked}
          testId={`flag-toggle-${flag.key}`}
        />
      </span>
    </div>
  );
}

// ─── Section header ───────────────────────────────────────────────────────────

function SectionHeader({ title }) {
  return (
    <h3 style={{
      margin: "20px 0 4px",
      fontSize: T.fsMd5,
      fontWeight: T.fwSemibold,
      color: T.dark,
      letterSpacing: -0.2,
    }}>
      {title}
    </h3>
  );
}

// ─── Main panel ───────────────────────────────────────────────────────────────

export function NavFeaturesPanel({ explainer, currentRole = "admin" } = {}) {
  const {
    updateFlag,
    updateNavItem,
    updateNavOrder,
    updateLocationTab,
    updateLocationTabOrder,
    updateLeaseTab,
    updateLeaseTabOrder,
    updateProjectsTab,
    updatePlacesTab,
    updateProjectsTabOrder,
    updateReportFlag,
    updateReportFlagOrder,
    updateAdminSection,
    refreshFromServer,
  } = useFeatureFlagContext();

  const [flags,              setFlags]              = useState([]);
  const [loading,            setLoading]            = useState(true);
  const [error,              setError]              = useState(null);
  const [busy,               setBusy]               = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [restoreBusy,        setRestoreBusy]        = useState(false);

  // Drag state for left_nav rows.
  const dragKeyRef          = useRef(null);
  // Drag state for location_tab rows (separate scope to prevent cross-section drops).
  const dragTabKeyRef       = useRef(null);
  // Drag state for lease_tab rows (separate scope to prevent cross-section drops).
  const dragLeaseTabKeyRef  = useRef(null);
  const dragProjectsTabKeyRef = useRef(null);
  // Drag state for report rows (separate scope to prevent cross-section drops).
  const dragReportKeyRef    = useRef(null);

  // Load all flags from the admin endpoint.
  const loadFlags = useCallback(() => {
    if (currentRole !== "admin") { setLoading(false); return; }
    setLoading(true);
    setError(null);
    fetch("/api/admin/feature-flags")
      .then((r) => {
        if (!r.ok) throw new Error(`Server responded ${r.status}`);
        return r.json();
      })
      .then((data) => {
        setFlags(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((err) => {
        log.error("NavFeaturesPanel: load failed", { err: String(err) });
        setError("Failed to load feature flags. Please try again.");
        setLoading(false);
      });
  }, [currentRole]);

  useEffect(() => { loadFlags(); }, [loadFlags]);

  // Optimistic toggle — update local state AND shared context immediately, revert on error.
  const handleToggle = useCallback(async (key, newEnabled) => {
    if (busy || currentRole !== "admin") return;

    // Client-side locked guard — mirrors the server-side check. The toggle is
    // already disabled in FlagRow for locked rows, but a direct call (or a
    // stale render) must not fire a request that the server would reject anyway.
    const targetRow = flags.find((r) => r.key === key);
    if (targetRow?.locked && !newEnabled) {
      setError("This section is locked and cannot be hidden.");
      return;
    }

    setBusy(true);
    setError(null);

    // Snapshot the current flags for potential revert.
    const prev = flags;
    const flagRow = prev.find((r) => r.key === key);
    if (flagRow?.surface === "places_tab") updatePlacesTab(key, newEnabled);
    setFlags((f) => f.map((row) => row.key === key ? { ...row, enabled: newEnabled } : row));

    // Update the shared FeatureFlagContext — nav flags update NavRail; tab flags update LocationDetail.
    const flagContextKey = flagRow?.surface === "left_nav"
      ? legacyFlagForNav(dbNavKey(key))
      : DB_KEY_TO_FLAG_KEY[key];
    if (flagContextKey) {
      updateFlag(key, { [flagContextKey]: newEnabled });
    }
    // For location_tab surface: propagate to locationTabs context state.
    if (flagRow?.surface === "left_nav") {
      updateNavItem(key, newEnabled);
    }
    if (flagRow?.surface === "location_tab") {
      updateLocationTab(key, newEnabled);
    }
    // For lease_tab surface: propagate to leaseTabs context state.
    if (flagRow?.surface === "lease_tab") {
      updateLeaseTab(key, newEnabled);
    }
    if (flagRow?.surface === "project_tab") {
      updateProjectsTab(key, newEnabled);
    }
    // For report surface: propagate to reportFlags context state.
    if (flagRow?.surface === "report") {
      updateReportFlag(key, newEnabled);
    }
    // For admin_section surface: propagate to adminSections context state.
    if (flagRow?.surface === "admin_section") {
      updateAdminSection(key, newEnabled);
    }

    try {
      const res = await fetch(`/api/admin/feature-flags/${key}`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ enabled: newEnabled }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Server responded ${res.status}`);
      }
      const updated = await res.json();
      if (flagRow?.surface === "places_tab") updatePlacesTab(key, updated.enabled);
      // Apply server's authoritative response.
      setFlags((f) => f.map((row) => row.key === key ? { ...row, ...updated } : row));
      if (flagRow?.surface === "left_nav" && typeof updated.enabled === "boolean") {
        updateNavItem(key, updated.enabled);
        if (flagContextKey) updateFlag(key, { [flagContextKey]: updated.enabled });
      }
      if (flagRow?.surface === "project_tab" && typeof updated.enabled === "boolean") {
        updateProjectsTab(key, updated.enabled);
      }
    } catch (err) {
      log.error("NavFeaturesPanel: toggle failed", { key, err: String(err) });
      // Revert local state.
      setFlags(prev);
      if (flagRow?.surface === "places_tab") updatePlacesTab(key, flagRow.enabled);
      // Revert context updates.
      if (flagContextKey) {
        const prevRow = prev.find((r) => r.key === key);
        if (prevRow) updateFlag(key, { [flagContextKey]: prevRow.enabled });
      }
      if (flagRow?.surface === "left_nav") {
        const prevRow = prev.find((r) => r.key === key);
        if (prevRow) updateNavItem(key, prevRow.enabled);
      }
      if (flagRow?.surface === "location_tab") {
        const prevRow = prev.find((r) => r.key === key);
        if (prevRow) updateLocationTab(key, prevRow.enabled);
      }
      if (flagRow?.surface === "lease_tab") {
        const prevRow = prev.find((r) => r.key === key);
        if (prevRow) updateLeaseTab(key, prevRow.enabled);
      }
      if (flagRow?.surface === "project_tab") {
        updateProjectsTab(key, flagRow.enabled);
      }
      if (flagRow?.surface === "report") {
        const prevRow = prev.find((r) => r.key === key);
        if (prevRow) updateReportFlag(key, prevRow.enabled);
      }
      if (flagRow?.surface === "admin_section") {
        const prevRow = prev.find((r) => r.key === key);
        if (prevRow) updateAdminSection(key, prevRow.enabled);
      }
      setError(err.message || "Toggle failed. Please try again.");
    } finally {
      setBusy(false);
    }
  }, [busy, currentRole, flags, updateFlag, updateNavItem, updateLocationTab, updateLeaseTab, updateProjectsTab, updatePlacesTab, updateReportFlag, updateAdminSection]);

  // PULSE-INFOCUS-RICH-CAROUSEL: string_value PATCH handler for flags that
  // carry a string_value instead of (or alongside) a boolean enabled toggle.
  // Used by the "In focus layout" segmented control (pulse.in_focus_layout).
  const handleStringValueChange = useCallback(async (key, newStringValue) => {
    if (busy) return;
    setBusy(true);
    setError(null);

    const prev = flags;
    setFlags((f) => f.map((row) => row.key === key ? { ...row, string_value: newStringValue } : row));

    // Propagate to shared FeatureFlagContext so PulsePage re-renders instantly.
    const flagContextKey = DB_KEY_TO_FLAG_KEY[key];
    if (flagContextKey) {
      updateFlag(key, { [flagContextKey]: newStringValue });
    }

    try {
      const res = await fetch(`/api/admin/feature-flags/${key}`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ string_value: newStringValue }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Server responded ${res.status}`);
      }
      const updated = await res.json();
      setFlags((f) => f.map((row) => row.key === key ? { ...row, ...updated } : row));
      if (key === "attendance-source" && updated.string_value) {
        emitAttendanceSourceChange();
      }
    } catch (err) {
      log.error("NavFeaturesPanel: string value change failed", { key, err: String(err) });
      setFlags(prev);
      if (flagContextKey) {
        const prevRow = prev.find((r) => r.key === key);
        if (prevRow) updateFlag(key, { [flagContextKey]: prevRow.string_value ?? 'classic' });
      }
      setError(err.message || "Update failed. Please try again.");
    } finally {
      setBusy(false);
    }
  }, [busy, flags, updateFlag]);

  // ─── Drag-to-reorder handlers (left_nav rows) ──────────────────────────────

  const handleDragStart = useCallback((key) => {
    dragKeyRef.current = key;
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback(async (targetKey) => {
    const sourceKey = dragKeyRef.current;
    dragKeyRef.current = null;

    if (!sourceKey || sourceKey === targetKey) return;
    if (busy) return;

    setBusy(true);
    setError(null);

    const leftNavFlags = flags
      .filter((f) => f.surface === "left_nav")
      .sort((a, b) => a.sort_order - b.sort_order);
    const sourceIdx = leftNavFlags.findIndex((f) => f.key === sourceKey);
    const targetIdx = leftNavFlags.findIndex((f) => f.key === targetKey);
    if (sourceIdx < 0 || targetIdx < 0) {
      setBusy(false);
      return;
    }

    const reordered = [...leftNavFlags];
    const [moved] = reordered.splice(sourceIdx, 1);
    reordered.splice(targetIdx, 0, moved);

    const prevFlags    = flags;
    const prevNavOrder = leftNavFlags.map((f) => dbNavKey(f.key));
    const reorderedKeys    = reordered.map((f) => f.key);
    const reorderedNavKeys = reordered.map((f) => dbNavKey(f.key));

    // Optimistic update: keep the moved item in the single flat order.
    setFlags((current) => {
      const nonLeft = current.filter((f) => f.surface !== "left_nav");
      const reorderedLeft = reorderedKeys.map((k) => current.find((f) => f.key === k));
      return [...reorderedLeft, ...nonLeft];
    });
    updateNavOrder(reorderedNavKeys);

    const payload = reordered.map((f, idx) => ({ key: f.key, sort_order: idx }));

    try {
      const res = await fetch("/api/admin/feature-flags/reorder", {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Server responded ${res.status}`);
      }
    } catch (err) {
      log.error("NavFeaturesPanel: reorder failed", { err: String(err) });
      setFlags(prevFlags);
      updateNavOrder(prevNavOrder);
      setError(err.message || "Reorder failed. Please try again.");
    } finally {
      setBusy(false);
    }
  }, [busy, flags, updateNavOrder]);

  const handleDragEnd = useCallback(() => {
    dragKeyRef.current = null;
  }, []);

  // ─── Drag-to-reorder handlers (location_tab rows) ──────────────────────────

  const handleTabDragStart = useCallback((key) => {
    dragTabKeyRef.current = key;
  }, []);

  const handleTabDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleTabDrop = useCallback(async (targetKey) => {
    const sourceKey = dragTabKeyRef.current;
    dragTabKeyRef.current = null;

    if (!sourceKey || sourceKey === targetKey) return;
    if (busy) return;

    setBusy(true);
    setError(null);

    const tabFlags  = flags.filter((f) => f.surface === "location_tab");
    const sourceIdx = tabFlags.findIndex((f) => f.key === sourceKey);
    const targetIdx = tabFlags.findIndex((f) => f.key === targetKey);
    if (sourceIdx < 0 || targetIdx < 0) {
      setBusy(false);
      return;
    }

    const reordered = [...tabFlags];
    const [moved] = reordered.splice(sourceIdx, 1);
    reordered.splice(targetIdx, 0, moved);

    const prevFlags       = flags;
    const prevTabOrder    = tabFlags.map((f) => f.key);
    const reorderedKeys   = reordered.map((f) => f.key);

    // Optimistic local update.
    setFlags((current) => {
      const nonTab    = current.filter((f) => f.surface !== "location_tab");
      const reorderedTab = reorderedKeys.map((k) => current.find((f) => f.key === k));
      return [...nonTab, ...reorderedTab];
    });
    // Update shared locationTabs context so LocationDetail re-renders immediately.
    updateLocationTabOrder(reorderedKeys);

    const payload = reordered.map((f, idx) => ({ key: f.key, sort_order: idx }));

    try {
      const res = await fetch("/api/admin/feature-flags/reorder", {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Server responded ${res.status}`);
      }
    } catch (err) {
      log.error("NavFeaturesPanel: tab reorder failed", { err: String(err) });
      setFlags(prevFlags);
      updateLocationTabOrder(prevTabOrder);
      setError(err.message || "Reorder failed. Please try again.");
    } finally {
      setBusy(false);
    }
  }, [busy, flags, updateLocationTabOrder]);

  const handleTabDragEnd = useCallback(() => {
    dragTabKeyRef.current = null;
  }, []);

  // ─── Drag-to-reorder handlers (lease_tab rows) ──────────────────────────────

  const handleLeaseTabDragStart = useCallback((key) => {
    dragLeaseTabKeyRef.current = key;
  }, []);

  const handleLeaseTabDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleLeaseTabDrop = useCallback(async (targetKey) => {
    const sourceKey = dragLeaseTabKeyRef.current;
    dragLeaseTabKeyRef.current = null;

    if (!sourceKey || sourceKey === targetKey) return;
    if (busy) return;

    setBusy(true);
    setError(null);

    const leaseTabFlags = flags.filter((f) => f.surface === "lease_tab");
    const sourceIdx     = leaseTabFlags.findIndex((f) => f.key === sourceKey);
    const targetIdx     = leaseTabFlags.findIndex((f) => f.key === targetKey);
    if (sourceIdx < 0 || targetIdx < 0) {
      setBusy(false);
      return;
    }

    const reordered = [...leaseTabFlags];
    const [moved] = reordered.splice(sourceIdx, 1);
    reordered.splice(targetIdx, 0, moved);

    const prevFlags          = flags;
    const prevLeaseTabOrder  = leaseTabFlags.map((f) => f.key);
    const reorderedKeys      = reordered.map((f) => f.key);

    setFlags((current) => {
      const nonLeaseTab    = current.filter((f) => f.surface !== "lease_tab");
      const reorderedLease = reorderedKeys.map((k) => current.find((f) => f.key === k));
      return [...nonLeaseTab, ...reorderedLease];
    });
    updateLeaseTabOrder(reorderedKeys);

    const payload = reordered.map((f, idx) => ({ key: f.key, sort_order: idx }));

    try {
      const res = await fetch("/api/admin/feature-flags/reorder", {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Server responded ${res.status}`);
      }
    } catch (err) {
      log.error("NavFeaturesPanel: lease tab reorder failed", { err: String(err) });
      setFlags(prevFlags);
      updateLeaseTabOrder(prevLeaseTabOrder);
      setError(err.message || "Reorder failed. Please try again.");
    } finally {
      setBusy(false);
    }
  }, [busy, flags, updateLeaseTabOrder]);

  const handleLeaseTabDragEnd = useCallback(() => {
    dragLeaseTabKeyRef.current = null;
  }, []);

  // ─── Drag-to-reorder handlers (report rows) ─────────────────────────────────

  const handleReportDragStart = useCallback((key) => {
    dragReportKeyRef.current = key;
  }, []);

  const handleReportDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleReportDrop = useCallback(async (targetKey) => {
    const sourceKey = dragReportKeyRef.current;
    dragReportKeyRef.current = null;

    if (!sourceKey || sourceKey === targetKey) return;
    if (busy) return;

    setBusy(true);
    setError(null);

    const reportFlags = flags.filter((f) => f.surface === "report")
      .sort((a, b) => a.sort_order - b.sort_order);
    const sourceIdx   = reportFlags.findIndex((f) => f.key === sourceKey);
    const targetIdx   = reportFlags.findIndex((f) => f.key === targetKey);
    if (sourceIdx < 0 || targetIdx < 0) {
      setBusy(false);
      return;
    }

    const reordered = [...reportFlags];
    const [moved] = reordered.splice(sourceIdx, 1);
    reordered.splice(targetIdx, 0, moved);

    const prevFlags       = flags;
    const prevReportOrder = reportFlags.map((f) => f.key);
    const reorderedKeys   = reordered.map((f) => f.key);

    // Optimistic local update — also refresh sort_order so reportFlagRows.sort() stays correct.
    setFlags((current) => {
      const nonReport       = current.filter((f) => f.surface !== "report");
      const reorderedReport = reorderedKeys.map((k, idx) => {
        const f = current.find((fl) => fl.key === k);
        return f ? { ...f, sort_order: idx + 1 } : null;
      }).filter(Boolean);
      return [...nonReport, ...reorderedReport];
    });
    // Update shared reportFlags context so ReportsPage re-renders immediately.
    updateReportFlagOrder(reorderedKeys);

    const payload = reordered.map((f, idx) => ({ key: f.key, sort_order: idx + 1 }));

    try {
      const res = await fetch("/api/admin/feature-flags/reorder", {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Server responded ${res.status}`);
      }
    } catch (err) {
      log.error("NavFeaturesPanel: report reorder failed", { err: String(err) });
      setFlags(prevFlags);
      updateReportFlagOrder(prevReportOrder);
      setError(err.message || "Reorder failed. Please try again.");
    } finally {
      setBusy(false);
    }
  }, [busy, flags, updateReportFlagOrder]);

  const handleReportDragEnd = useCallback(() => {
    dragReportKeyRef.current = null;
  }, []);

  const handleProjectsTabDrop = useCallback(async (targetKey) => {
    const sourceKey = dragProjectsTabKeyRef.current;
    dragProjectsTabKeyRef.current = null;
    if (currentRole !== "admin" || busy || !sourceKey || sourceKey === targetKey) return;
    const rows = flags.filter((row) => row.surface === "project_tab")
      .sort((a, b) => a.sort_order - b.sort_order || a.key.localeCompare(b.key));
    const from = rows.findIndex((row) => row.key === sourceKey);
    const to = rows.findIndex((row) => row.key === targetKey);
    if (from < 0 || to < 0) return;
    const previousKeys = rows.map((row) => row.key);
    const [moved] = rows.splice(from, 1);
    rows.splice(to, 0, moved);
    const reordered = rows.map((row, sort_order) => ({ ...row, sort_order }));
    setBusy(true);
    setError(null);
    setFlags((prev) => [...prev.filter((row) => row.surface !== "project_tab"), ...reordered]);
    updateProjectsTabOrder(reordered.map((row) => row.key));
    try {
      const res = await fetch("/api/admin/feature-flags/reorder", {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(reordered.map(({ key, sort_order }) => ({ key, sort_order }))),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Server responded ${res.status}`);
      }
    } catch (err) {
      setFlags(flags);
      updateProjectsTabOrder(previousKeys);
      setError(err.message || "Reorder failed. Please try again.");
    } finally { setBusy(false); }
  }, [busy, currentRole, flags, updateProjectsTabOrder]);

  // ─── Restore defaults ───────────────────────────────────────────────────────

  const handleRestoreDefaults = useCallback(async () => {
    setRestoreBusy(true);
    setError(null);
    try {
      const res = await fetch("/api/admin/feature-flags/restore-defaults", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Server responded ${res.status}`);
      }
      setShowRestoreConfirm(false);
      // Reload panel-local state AND sync the shared FeatureFlagContext so
      // NavRail and LocationDetail immediately reflect the canonical baseline
      // without a full page reload.
      loadFlags();
      refreshFromServer();
    } catch (err) {
      log.error("NavFeaturesPanel: restore-defaults failed", { err: String(err) });
      setError(err.message || "Restore failed. Please try again.");
      setShowRestoreConfirm(false);
    } finally {
      setRestoreBusy(false);
    }
  }, [loadFlags, refreshFromServer]);

  // ─── Render ─────────────────────────────────────────────────────────────────

  if (currentRole !== "admin") {
    return <p role="alert">Only administrators can edit Navigation Configuration.</p>;
  }

  if (loading) {
    return (
      <div data-testid="nav-features-panel">
        <h2 style={{ margin: "0 0 10px", color: T.dark, fontSize: T.fsLg5 }}>Navigation &amp; Features</h2>
        {explainer}
        <div data-testid="nav-features-loading" style={{ color: T.mid, fontSize: T.fsBase }}>
          Loading…
        </div>
      </div>
    );
  }

  const previewModeFlag  = flags.find((f) => f.key === "preview_mode");
  const leftNavFlags     = flags.filter((f) => f.surface === "left_nav");
  const topNavFlags      = flags.filter((f) => f.surface === "top_nav");
  const locationTabFlags = flags.filter((f) => f.surface === "location_tab");
  const leaseTabFlags    = flags.filter((f) => f.surface === "lease_tab")
    .sort((a, b) => a.sort_order - b.sort_order);
  const reportFlagRows   = flags.filter((f) => f.surface === "report")
    .sort((a, b) => a.sort_order - b.sort_order);
  const settingFlags     = flags.filter((f) => f.surface === "setting" && f.key !== "preview_mode" && f.key !== "ui_v2")
    .sort((a, b) => a.sort_order - b.sort_order);
  const radarSettingFlags = settingFlags.filter((f) => f.key.startsWith("radar_"));
  const generalSettingFlags = settingFlags.filter((f) => !f.key.startsWith("radar_"));
  // User and role governance has its own Users & Access IA. Keep those
  // surfaces out of the global/system feature-controls panel.
  const adminSectionFlags = flags.filter((f) => f.surface === "admin_section"
    && !["users_sso", "roles", "activity"].includes(f.key))
    .sort((a, b) => a.sort_order - b.sort_order);

  return (
    <div data-testid="nav-features-panel">
      <h2 style={{ margin: "0 0 10px", color: T.dark, fontSize: T.fsLg5 }}>Navigation &amp; Features</h2>
      {explainer}
      {error && (
        <ErrorBanner error={error} onDismiss={() => setError(null)} />
      )}

      {/* Preview mode master toggle */}
      {previewModeFlag && (
        <div
          data-testid="preview-mode-row"
          style={{
            display: "flex", alignItems: "center", gap: 12,
            padding: "12px 16px",
            background: T.lightGray,
            borderRadius: 8,
            marginBottom: 8,
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: T.fsBase, fontWeight: T.fwSemibold, color: T.dark }}>
              Preview mode
            </div>
            <div style={{ fontSize: T.fsSm, color: T.mid, marginTop: 2 }}>
              When ON: admins see disabled items in the nav regardless of their
              individual toggle. When OFF: disabled items are hidden from everyone,
              including admins, and their routes redirect away.
            </div>
          </div>
          <SlimToggle
            checked={previewModeFlag.enabled}
            onChange={(val) => handleToggle("preview_mode", val)}
            disabled={busy}
            testId="flag-toggle-preview_mode"
          />
        </div>
      )}

      {/* Left navigation section — one flat drag-to-reorder list */}
      {leftNavFlags.length > 0 && (
        <>
          <SectionHeader title="Left navigation" />
          <div data-testid="left-nav-section">
            {[...leftNavFlags]
              .sort((a, b) => a.sort_order - b.sort_order)
              .map((flag) => (
                <React.Fragment key={flag.key}>
                <FlagRow
                  flag={flag}
                  onToggle={handleToggle}
                  busy={busy}
                  draggable={!busy}
                  onDragStart={() => handleDragStart(flag.key)}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(flag.key)}
                  onDragEnd={handleDragEnd}
                />
                {flag.key === "places" && (
                  <div data-testid="places-lens-controls" style={{ marginLeft: 32 }}>
                    {PLACES_LENSES.map((lens) => {
                      const row = flags.find((flag) => flag.surface === "places_tab" && flag.key === lens.key);
                      return row ? { ...row, label: lens.label, description: lens.description ?? row.description } : null;
                    }).filter(Boolean)
                      .map((row) => <FlagRow key={row.key} flag={row}
                        onToggle={handleToggle} busy={busy} draggable={false} />)}
                  </div>
                )}
                </React.Fragment>
              ))}
          </div>
        </>
      )}

      {/* Top navigation section — not reorderable */}
      {topNavFlags.length > 0 && (
        <>
          <SectionHeader title="Top navigation" />
          <div data-testid="top-nav-section">
            {topNavFlags.map((flag) => (
              <FlagRow
                key={flag.key}
                flag={flag}
                onToggle={handleToggle}
                busy={busy}
                draggable={false}
              />
            ))}
          </div>
        </>
      )}

      {/* Offices tabs section — rows are drag-to-reorder (separate scope from left_nav) */}
      {locationTabFlags.length > 0 && (
        <>
          <SectionHeader title="Offices tabs" />
          <div data-testid="location-tabs-section">
            {locationTabFlags.map((flag) => (
              <FlagRow
                key={flag.key}
                flag={flag}
                onToggle={handleToggle}
                busy={busy}
                draggable={!busy}
                onDragStart={() => handleTabDragStart(flag.key)}
                onDragOver={handleTabDragOver}
                onDrop={() => handleTabDrop(flag.key)}
                onDragEnd={handleTabDragEnd}
              />
            ))}
          </div>
        </>
      )}

      {/* Leases tabs section — rows are drag-to-reorder (separate scope from other sections) */}
      {leaseTabFlags.length > 0 && (
        <>
          <SectionHeader title="Leases tabs" />
          <div data-testid="lease-tabs-section">
            {leaseTabFlags.map((flag) => (
              <FlagRow
                key={flag.key}
                flag={flag}
                onToggle={handleToggle}
                busy={busy}
                draggable={!busy}
                onDragStart={() => handleLeaseTabDragStart(flag.key)}
                onDragOver={handleLeaseTabDragOver}
                onDrop={() => handleLeaseTabDrop(flag.key)}
                onDragEnd={handleLeaseTabDragEnd}
              />
            ))}
          </div>
        </>
      )}

      {flags.some((row) => row.surface === "project_tab") && (
        <>
          <SectionHeader title="Projects tabs" />
          <div data-testid="projects-tabs-section">
            <p style={{ color: T.mid, fontSize: T.fsSm }}>Portfolio is always visible and remains the landing page. Drag any tab, including Portfolio, to change its position.</p>
            {flags.filter((row) => row.surface === "project_tab")
              .sort((a, b) => a.sort_order - b.sort_order || a.key.localeCompare(b.key))
              .map((flag) => (
                <FlagRow key={flag.key}
                  flag={{ ...flag, locked: PROJECTS_TABS.find((row) => row.key === flag.key)?.locked || flag.locked }}
                  onToggle={handleToggle} busy={busy} draggable={!busy}
                  onDragStart={() => { dragProjectsTabKeyRef.current = flag.key; }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleProjectsTabDrop(flag.key)}
                  onDragEnd={() => { dragProjectsTabKeyRef.current = null; }}
                />
              ))}
          </div>
        </>
      )}

      {/* Reports section - rows are drag-to-reorder (separate scope from other sections) */}
      {reportFlagRows.length > 0 && (
        <>
          <SectionHeader title="Reports" />
          <div data-testid="reports-section">
            {reportFlagRows.map((flag) => (
              <FlagRow
                key={flag.key}
                flag={flag}
                onToggle={handleToggle}
                busy={busy}
                draggable={!busy}
                onDragStart={() => handleReportDragStart(flag.key)}
                onDragOver={handleReportDragOver}
                onDrop={() => handleReportDrop(flag.key)}
                onDragEnd={handleReportDragEnd}
              />
            ))}
          </div>
        </>
      )}

      {/* Settings section — non-reorderable feature toggles (e.g. pdf_abstraction)
          plus the In focus layout segmented control (pulse.in_focus_layout). */}
      {settingFlags.length > 0 && (
        <>
          {radarSettingFlags.length > 0 && (
            <>
              <SectionHeader title="Radar" />
              <div data-testid="radar-settings-section">
                {radarSettingFlags.map((flag) => (
                  <FlagRow
                    key={flag.key}
                    flag={flag}
                    onToggle={handleToggle}
                    busy={busy}
                    draggable={false}
                  />
                ))}
              </div>
            </>
          )}
          <SectionHeader title="Settings" />
          <div data-testid="settings-section">
            {generalSettingFlags.map((flag) => {
              // The In focus layout flag gets a 3-way segmented control
              // (Classic | Rich | Off) instead of a boolean toggle.
              if (flag.key === "pulse.in_focus_layout") {
                const currentVal = flag.string_value ?? 'classic';
                const OPTIONS = [
                  { value: 'classic', label: 'Classic' },
                  { value: 'rich',    label: 'Rich' },
                  { value: 'off',     label: 'Off' },
                ];
                return (
                  <div
                    key={flag.key}
                    data-testid="flag-row-pulse.in_focus_layout"
                    style={{
                      display: "flex", alignItems: "center", gap: 12,
                      padding: "12px 16px",
                      background: T.lightGray,
                      borderRadius: 8,
                      marginBottom: 8,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: T.fsBase, fontWeight: T.fwSemibold, color: T.dark }}>
                        {flag.label}
                      </div>
                      {flag.description && (
                        <div style={{ fontSize: T.fsSm, color: T.mid, marginTop: 2 }}>
                          {flag.description}
                        </div>
                      )}
                    </div>
                    <div
                      data-testid="infocus-layout-segcontrol"
                      style={{
                        display:      "inline-flex",
                        background:   "var(--v2-hair)",
                        borderRadius: 10,
                        padding:      3,
                        gap:          2,
                        flexShrink:   0,
                      }}
                    >
                      {OPTIONS.map((opt) => {
                        const active = currentVal === opt.value;
                        return (
                          <button
                            key={opt.value}
                            data-testid={`infocus-layout-btn-${opt.value}`}
                            disabled={busy}
                            onClick={() => handleStringValueChange(flag.key, opt.value)}
                            aria-pressed={active}
                            style={{
                              border:       0,
                              height:       T.toolbarSegH,
                              padding:      "0 12px",
                              borderRadius: 8,
                              fontSize:     T.fsSm,
                              fontWeight:   T.fwBold,
                              cursor:       busy ? "not-allowed" : "pointer",
                              whiteSpace:   "nowrap",
                              background:   active ? "var(--v2-card)"   : "transparent",
                              color:        active ? "var(--v2-accent)" : "var(--v2-mid)",
                              boxShadow:    active ? "0 1px 4px rgba(16,36,58,.1)" : "none",
                              transition:   "all .15s",
                            }}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              }
              if (flag.key === "attendance-source") {
                const currentVal = flag.string_value === "envoy_bridge"
                  ? "envoy_bridge"
                  : "alta_verkada";
                const OPTIONS = [
                  { value: "alta_verkada", label: "Alta/Verkada" },
                  { value: "envoy_bridge", label: "Envoy" },
                ];
                return (
                  <div
                    key={flag.key}
                    data-testid="flag-row-attendance-source"
                    style={{
                      display: "flex", alignItems: "center", gap: 12,
                      padding: "12px 16px",
                      background: T.lightGray,
                      borderRadius: 8,
                      marginBottom: 8,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: T.fsBase, fontWeight: T.fwSemibold, color: T.dark }}>
                        {flag.label}
                      </div>
                      {flag.description && (
                        <div style={{ fontSize: T.fsSm, color: T.mid, marginTop: 2 }}>
                          {flag.description}
                        </div>
                      )}
                    </div>
                    <div
                      data-testid="attendance-source-segcontrol"
                      style={{
                        display: "inline-flex",
                        background: "var(--v2-hair)",
                        borderRadius: 10,
                        padding: 3,
                        gap: 2,
                        flexShrink: 0,
                      }}
                    >
                      {OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          data-testid={`attendance-source-btn-${opt.value}`}
                          disabled={busy}
                          onClick={() => handleStringValueChange(flag.key, opt.value)}
                          aria-pressed={currentVal === opt.value}
                          style={{
                            border: 0,
                            height: T.toolbarSegH,
                            padding: "0 12px",
                            borderRadius: 8,
                            background: currentVal === opt.value ? T.white : "transparent",
                            color: currentVal === opt.value ? T.dark : T.mid,
                            fontSize: T.fsSm,
                            fontWeight: T.fwSemibold,
                            cursor: busy ? "wait" : "pointer",
                            boxShadow: currentVal === opt.value ? T.shadowSm : "none",
                          }}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              }
              return (
                <FlagRow
                  key={flag.key}
                  flag={flag}
                  onToggle={handleToggle}
                  busy={busy}
                  draggable={false}
                />
              );
            })}
          </div>
        </>
      )}

      {/* Other admin surfaces remain global/system visibility controls. User,
          role, and activity governance are intentionally not feature toggles. */}
      {adminSectionFlags.length > 0 && (
        <>
          <SectionHeader title="Other admin surfaces" />
          <div style={{ fontSize: T.fsSm, color: T.mid, margin: "-2px 0 4px" }}>
            Global and system surfaces shown in the Admin sidebar.
          </div>
          <div data-testid="admin-sections-section">
            {adminSectionFlags.map((flag) => (
              <FlagRow
                key={flag.key}
                flag={flag}
                onToggle={handleToggle}
                busy={busy}
                draggable={false}
                useAdminIcon
              />
            ))}
          </div>
        </>
      )}

      {flags.length === 0 && !loading && (
        <div style={{ color: T.mid, fontSize: T.fsBase, paddingTop: 16 }}>
          No feature flags found.
        </div>
      )}

      {/* Restore defaults - visually separated from drag lists */}
      <div
        style={{
          marginTop: 28,
          paddingTop: 20,
          borderTop: `1px solid ${T.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
        }}
      >
        <div>
          <div style={{ fontSize: T.fsBase, fontWeight: T.fwSemibold, color: T.dark }}>
            Restore defaults
          </div>
          <div style={{ fontSize: T.fsSm, color: T.mid, marginTop: 2 }}>
            Reset navigation, Offices, Leases, Projects, reports and admin sections to their shipped order and visibility defaults. Preview mode is unchanged.
          </div>
        </div>
        <button
          type="button"
          data-testid="restore-defaults-button"
          onClick={() => setShowRestoreConfirm(true)}
          disabled={busy || restoreBusy}
          style={{
            padding: "8px 16px",
            fontSize: T.fsSm5,
            fontWeight: T.fwSemibold,
            color: T.blue,
            background: T.white,
            border: `1px solid ${T.blue}`,
            borderRadius: 6,
            cursor: busy || restoreBusy ? "not-allowed" : "pointer",
            flexShrink: 0,
            opacity: busy || restoreBusy ? 0.6 : 1,
          }}
        >
          Restore defaults
        </button>
      </div>

      {/* Restore defaults confirmation modal */}
      {showRestoreConfirm && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 10000,
            background: "rgba(0,0,0,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 20,
          }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !restoreBusy) {
              setShowRestoreConfirm(false);
            }
          }}
          data-testid="restore-defaults-modal-overlay"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="restore-defaults-modal-title"
            data-testid="restore-defaults-modal"
            style={{
              background: T.white,
              color: T.dark,
              borderRadius: 10,
              boxShadow: T.shadowModal,
              width: "100%", maxWidth: 460,
              overflow: "hidden",
            }}
          >
            {/* Modal header */}
            <div style={{
              background: T.white,
              borderBottom: `1px solid ${T.border}`,
              padding: "16px 20px",
            }}>
              <h3
                id="restore-defaults-modal-title"
                style={{
                  margin: 0,
                  fontSize: T.fsMd5,
                  fontWeight: T.fwSemibold,
                  color: T.dark,
                }}
              >
                Restore defaults?
              </h3>
            </div>

            {/* Body */}
            <div style={{ padding: 20 }}>
              <p style={{ margin: "0 0 16px", fontSize: T.fsBase5, color: T.dark, lineHeight: 1.5 }}>
                This will reset navigation, Offices, Leases, Projects, reports and admin
                sections to their shipped order and visibility defaults. Contracts and
                Risk Register will be hidden.
              </p>
              <p style={{ margin: "0 0 20px", fontSize: T.fsBase5, color: T.mid, lineHeight: 1.5 }}>
                Preview mode is not affected. This action is recorded in the audit log.
              </p>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button
                  type="button"
                  data-testid="restore-defaults-cancel-button"
                  onClick={() => setShowRestoreConfirm(false)}
                  disabled={restoreBusy}
                  style={{
                    padding: "7px 14px",
                    fontSize: T.fsSm5, fontWeight: T.fwSemibold,
                    color: T.dark, background: T.white,
                    border: `1px solid ${T.lightGray}`, borderRadius: 6,
                    cursor: restoreBusy ? "not-allowed" : "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-testid="restore-defaults-confirm-button"
                  onClick={handleRestoreDefaults}
                  disabled={restoreBusy}
                  style={{
                    padding: "7px 14px",
                    fontSize: T.fsSm5, fontWeight: T.fwBold,
                    color: T.white, background: T.blue,
                    border: "none", borderRadius: 6,
                    cursor: restoreBusy ? "not-allowed" : "pointer",
                    opacity: restoreBusy ? 0.7 : 1,
                  }}
                >
                  {restoreBusy ? "Restoring…" : "Restore defaults"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
